import React, { useEffect, useState } from 'react';
import { array, bool, func, number, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { propTypes } from '../../util/types';
import {
  sendVerificationEmail,
  hasCurrentUserErrors,
  fetchTodaysAvailability,
  getStripeUserInfo,
} from '../../ducks/user.duck';
import { logout, authenticationInProgress, login, clearLoginError } from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { Topbar } from '../../components';
import { isCorporateCustomer, isDoctorApproved, isIndividualUser } from '../../util/dataExtractors';
import { useIntl } from '../../util/reactIntl';
import Cookies from 'js-cookie';
import css from './TopbarContainer.module.css';
import moment from 'moment';
import { queryOwnListings } from '../ManageListingsPage/ManageListingsPage.duck';
import { isArrayLength } from '../../util/genericHelpers';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routing/routeConfiguration';
import { fetchEventsData, fetchEvents } from '../../ducks/strapi.duck';
import { ClientOnly, ConsentBannerHelper } from '../../components/ConsentWrapper/ConsentWrapper';

// TopbarContainerComponent
export const TopbarContainerComponent = props => {
  const {
    authInProgress,
    currentPage,
    currentSearchParams,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    history,
    isAuthenticated,
    authScopes,
    loginInProgress,
    loginError,
    hasGenericError,
    location,
    notificationCount,
    onLogout,
    onSubmitLogin,
    onClearLoginError,
    onManageDisableScrolling,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    isHeaderSticky,
    staticPageHeader,
    currentUserHasPendingListings,
    isPostLandingPage = false,
    hideVerificationModal = false,
    showAvailabilityModal,
    dashboardHeader,
    isFullWidthHeader,
    headerWithoutShadow,
    events,
    ...rest
  } = props;

  const intl = useIntl();
  const dispatch = useDispatch();

  const isCustomer = isIndividualUser(currentUser);
  const isValidCorporateCustomer = isCorporateCustomer(currentUser);

  const approvalContent = intl.formatMessage({
    id: 'LandingPage.approvalContent',
  });

  const [isModalClosedToday, setIsModalClosedToday] = useState(false);
  const [ownListing, setOwnListing] = useState(false);

  const fetchOwnListings = async () => {
    if (isAuthenticated) {
      const ownListings = await dispatch(queryOwnListings());
      setOwnListing(isArrayLength(ownListings) && ownListings[0]);
    }
  };

  useEffect(() => {
    const fetchEventsData = async () => {
      try {
        await dispatch(
          fetchEvents({
            fetchSinglePage: true,
            fetchOnlySlug: true,
          })
        );
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };

    fetchEventsData();
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const initialize = async () => {
      if (!isCustomer) {
        dispatch(fetchTodaysAvailability({}));
      }

      const lastModalInteractionDate = Cookies.get('lastModalInteractionDate');
      const today = moment().startOf('day');
      const lastInteractionDay = lastModalInteractionDate
        ? moment(lastModalInteractionDate).startOf('day')
        : null;

      setIsModalClosedToday(lastInteractionDay && today.isSame(lastInteractionDay));

      if (currentUser?.stripeAccount?.attributes?.stripeAccountId) {
        dispatch(
          getStripeUserInfo({ stripeId: currentUser.stripeAccount.attributes.stripeAccountId })
        );
      }

      if (location.pathname === '/') {
        const redirectPath = createResourceLocatorString(
          'LandingPage',
          routeConfiguration(),
          {},
          {}
        );
        history.push(redirectPath);
      }

      await fetchOwnListings();
    };

    initialize();
  }, [isAuthenticated, currentUser, location.pathname]);

  const handleDoItLater = () => {
    Cookies.set('lastModalInteractionDate', moment().toDate(), { expires: 1 });
    setIsModalClosedToday(true);
  };

  useEffect(() => {
    if (!isAuthenticated || !currentUser) return;

    const stripeId = currentUser?.stripeAccount?.attributes?.stripeAccountId;

    if (stripeId) {
      dispatch(getStripeUserInfo({ stripeId }));
    }

    if (location.pathname === '/') {
      navigateToHomepage();
    }

    function navigateToHomepage() {
      const redirectPath = createResourceLocatorString('LandingPage', routeConfiguration(), {}, {});
      history.push(redirectPath);
    }
  }, [isAuthenticated, currentUser?.stripeAccount?.attributes?.stripeAccountId, location.pathname]);

  return (
    <>
      <Topbar
        authInProgress={authInProgress}
        currentPage={currentPage}
        currentSearchParams={currentSearchParams}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        history={history}
        isAuthenticated={isAuthenticated}
        authScopes={authScopes}
        location={location}
        ownListing={ownListing}
        isModalClosedToday={isModalClosedToday}
        handleDoItLater={handleDoItLater}
        showAvailabilityModal={showAvailabilityModal}
        notificationCount={notificationCount}
        onLogout={onLogout}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
        showGenericError={hasGenericError}
        isHeaderSticky={isHeaderSticky}
        staticPageHeader={staticPageHeader}
        hideVerificationModal={hideVerificationModal}
        dashboardHeader={dashboardHeader}
        isFullWidthHeader={isFullWidthHeader}
        headerWithoutShadow={headerWithoutShadow}
        events={events}
        loginInProgress={loginInProgress}
        loginError={loginError}
        onSubmitLogin={onSubmitLogin}
        onClearLoginError={onClearLoginError}
        {...rest}
      />
      {isPostLandingPage &&
      isAuthenticated &&
      !isDoctorApproved(currentUser) &&
      currentUserHasPendingListings ? (
        <div className={css.profileVerification}>{approvalContent}</div>
      ) : null}
      {/* <ClientOnly>
        <ConsentBannerHelper />
      </ClientOnly> */}
    </>
  );
};

TopbarContainerComponent.defaultProps = {
  currentPage: null,
  currentSearchParams: null,
  currentUser: null,
  currentUserHasOrders: null,
  notificationCount: 0,
  sendVerificationEmailError: null,
  authScopes: null,
  logoVariant: null,
};

TopbarContainerComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentPage: string,
  currentSearchParams: object,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  isAuthenticated: bool.isRequired,
  authScopes: array,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  hasGenericError: bool.isRequired,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({ state: object }).isRequired,
  logoVariant: string,
  events: array,
};

const mapStateToProps = state => {
  const { isAuthenticated, logoutError, loginInProgress, loginError = null, authScopes } =
    state.auth || {};
  const {
    currentUser,
    currentUserHasPendingListings,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserNotificationCount: notificationCount,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showAvailabilityModal,
  } = state.user;

  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  const { events } = fetchEventsData(state) || {};

  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasPendingListings,
    currentUserHasListings,
    currentUserHasOrders,
    notificationCount,
    isAuthenticated,
    authScopes,
    loginInProgress,
    loginError,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
    showAvailabilityModal,
    events,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onLogout: () => dispatch(logout({ history: ownProps.history, redirectTo: '/' })),
  onSubmitLogin: ({ email, password }) => {
    const history = ownProps?.history;
    return dispatch(login(email, password)).then(user => {
      if (user?.attributes && history) {
        const menopauseItems =
          user.attributes?.profile?.protectedData?.onboardingData?.menopauseItems;
        const path =
          Array.isArray(menopauseItems) && menopauseItems.includes('familyProgram')
            ? createResourceLocatorString('Homepage', routeConfiguration(), {}, {})
            : createResourceLocatorString('MenopauseResourcePage', routeConfiguration(), {}, {});
        history.push(path);
      }
    });
  },
  onClearLoginError: () => dispatch(clearLoginError()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
});

const TopbarContainer = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(TopbarContainerComponent);

export default TopbarContainer;
